import React, {ReactElement, useEffect, useState} from 'react'
import {SettingCardProps} from './SettingCard.props'
import './SettingCard.styles.css';
import {Box, FormControlLabel, IconButton, Stack, Switch, Tooltip} from "@mui/material";
import {enablePurge, getSettings, updateAutomationRuleSettings, updateSetting} from '../../../api/emailTriage';
import {styled} from "@mui/material/styles";
import {Toast} from "../../atoms/Toast";
import {defaultSettings, useRoleContext} from "../../../context/RoleContext";
import {
  SETTING_BLOCK_LIST,
  SETTING_TYPE_AUTOMATION_RULE,
  SETTING_TYPE_REPORTED_EMAILS_ACTIONS
} from "../../../constants/contants";
import Skeleton from '@mui/material/Skeleton';
import {LoadingPage} from "../../organisms/LoadingPage";
import HelpIcon from "@mui/icons-material/Help";
import {Modal} from "../../atoms/Modal";

export const SettingCard: React.FC<SettingCardProps> = ({
    title,
    description,
    status,
    type,
    component,
    loading,
    showDisableDialog=false,
    showEnableDialog=false,
    disableDialogContent=<></>,
    disableDialogTitle="",
    onDisabled= () => {},
    onEnableToggle = () => {},
    titleFlexDirection="column",
    isSwitchUsed = true,
}) => {
  const [settingState, changeSettingState] = useState(false)
  const [toastValue, setToastValue] = useState({type: '', message: '', isOpen: false, toastId: 0})
  const [modalID, setModalID] = useState(1)
  const { setting, setSetting } = useRoleContext()
  const [loadingPage, setLoadingPage] = useState(false)

  const [isOpenDialog, setIsOpenDialog] = useState(false)
  const [isOpenOffDialog, setIsOpenOffDialog] = useState(false)


  const GreenSwitch = styled(Switch)(({ theme }) => ({
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: "#008545",
      '&:hover': {
        backgroundColor: "#B3DAC7",
      },
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: "#B3DAC7",
    },
  }));

  const renderDisconnectTenantContentModal = () => (
        <Box className={"modal-setting-disconnect-tenant-content"}>
            <p style={{margin: 0}}>When you disconnect, your Microsoft 365 account will also be logged out. Are you sure you want to disconnect your organisation's tenant from Phish Focus? </p>
            <Box className={"highlighted-content"}>
                <span>All created data will be <strong>removed.</strong></span>
                <span style={{margin: "8px 0"}}>Phish Clear or Block List feature enabled will be <strong>disabled.</strong></span>
                <span>Automation Rules with Phish Clear or Block List enabled will be <strong>disabled.</strong></span>
            </Box>
        </Box>
    )

  const changeSetting = (newStatus:any) => {
    const isChecked = newStatus;
    const params = { settingType: type, settingValue: isChecked };

    const afterUpdate = () => {
      getSettings().then((response) => {
        const newSettings = { ...defaultSettings };
        response.data.forEach((item: any) =>
          Object.assign(newSettings, { [item.setting_type]: item.setting_value })
        );
        setSetting(response.data.length > 0 ? newSettings : defaultSettings);
        if (isChecked) {
          onEnableToggle();
        }
      });
      setLoadingPage(false);
    };

    setLoadingPage(true);
    switch(type) {
      case SETTING_TYPE_REPORTED_EMAILS_ACTIONS: {
        updateSetting(params).then((response:any) => {
          if (response.status === 200) {
            setModalID(modalID + 1);
            setToastValue({ type: 'success', message: 'Update setting successfully!', isOpen: true, toastId: modalID });
            changeSettingState(isChecked);
          } else {
            setModalID(modalID + 1);
            setToastValue({ type: 'error', message: 'Something went wrong!', isOpen: true, toastId: modalID });
          }
          afterUpdate();
        }).catch(e => {
          console.error(e);
          setLoadingPage(false);
        });
        break;
      }
      case SETTING_TYPE_AUTOMATION_RULE: {
        updateAutomationRuleSettings(params).then((response) => {
          if (response.status === 200) {
            setModalID(modalID + 1);
            setToastValue({ type: 'success', message: 'Update setting successfully!', isOpen: true, toastId: modalID });
            changeSettingState(isChecked);
          } else {
            setModalID(modalID + 1);
            setToastValue({ type: 'error', message: 'Something went wrong!', isOpen: true, toastId: modalID });
          }
          afterUpdate();
        }).catch(e => {
          console.error(e);
          setLoadingPage(false);
        });
        break;
      }
      case SETTING_BLOCK_LIST: {
        updateSetting(params).then((response) => {
          if (response.status === 200) {
            setModalID(modalID + 1);
            setToastValue({ type: 'success', message: 'Update setting successfully!', isOpen: true, toastId: modalID });
            changeSettingState(isChecked);
          } else {
            setModalID(modalID + 1);
            setToastValue({ type: 'error', message: 'Something went wrong!', isOpen: true, toastId: modalID });
          }
          afterUpdate();
        }).catch(e => {
          console.error(e);
          setLoadingPage(false);
        });
        break;
      }
    }
  }

  const onValueOff = () => {
    changeSetting(false)
    setIsOpenOffDialog(false)
    onDisabled()
  }

  const onValueOn = () => {
    changeSetting(true)
    setIsOpenDialog(false)
  }

  const onStatusChange = (newStatus:any) => {
    if(newStatus === false && showDisableDialog) {
      setIsOpenOffDialog(true)
    } else if(newStatus === true && showEnableDialog) {
      setIsOpenDialog(true)
    } else {
      changeSetting(newStatus)
    }
  }


  useEffect(() => {
    changeSettingState(status)
  }, [status])

  const tooltipContent = {
    [SETTING_BLOCK_LIST]:
      'This feature helps your mail server prevent malicious or spam emails from reaching your users’ inbox. Enable Block List and connect to your Microsoft 365 tenant to manage a unique list of blocklist entries for your organisation.',
    [SETTING_TYPE_REPORTED_EMAILS_ACTIONS]: 'Allow to remove emails in users\' inbox',
  };

  const renderTitle = (title?: string | ReactElement) => {
    if (typeof title === "string") {
      return <Box className='setting-content-title' sx={{
        fontFamily: 'Montserrat',
        fontStyle: 'normal',
        fontWeight: '700',
        fontSize: '16px',
        color: '#212121',
        marginBottom: '10px',
        marginTop: '25px'
      }}>{title}</Box>
    }

    return title
  }

  return (
      <Box className='setting-wrapper' sx={{
        background: '#fff',
        marginBottom: '20px',
        border: '1px solid #E0E0E0',
        borderRadius: '4px',
        minHeight: '80px',
        boxSizing: 'border-box'
      }}>
        <Box className='setting-content' sx={{
          margin: '16px'
        }}>
          <Stack direction={titleFlexDirection}>
            {
              loading ? <Skeleton animation="wave" sx={{ width: '200px', height: '20px' }}/> :
                renderTitle(title)
            }
            {
              isSwitchUsed ? (
                  loading ? <Skeleton animation="wave" sx={{ width: '350px', height: '40px' }}/> :
                <Box className='setting-content-detail' sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: '-8px',
                  fontFamily: 'Montserrat',
                  fontStyle: 'normal',
                  fontWeight: '600',
                  fontSize: '14px',
                }}>
                  <FormControlLabel className={`status-setting ${settingState ? 'enabled' : 'disabled'}`} control={
                    <GreenSwitch checked={settingState}
                                 onChange={(newStatus: any) => onStatusChange(newStatus.target.checked)}/>
                  } label={description} sx={{ marginRight: "10px" }}/>
                  {
                    type === SETTING_TYPE_REPORTED_EMAILS_ACTIONS || type === SETTING_BLOCK_LIST ?
                      <Tooltip placement={"top"} title={tooltipContent[type]}>
                        <IconButton className='info-action'><HelpIcon fontSize={'small'}
                                                                      htmlColor={"#193051"}/></IconButton>
                      </Tooltip> : null
                  }
                </Box>
              ) : <></>
            }
          </Stack>
          {settingState && component}

        </Box>
        <Toast isOpen={toastValue.isOpen} type={toastValue.type} message={toastValue.message} toastId={toastValue.toastId} autoHideDuration={2000}/>
        <LoadingPage open={loadingPage}/>

        <Modal idModal="modal-setting-on-click"
                   openDialog={isOpenDialog}
                   variant={"delete"}
                   title={"Remove account"}
                   actionAgree={() => onValueOn()}
                   actionCancel={() => setIsOpenDialog(false)}
                   textAgree={"Enable"}
                   onClose={() => setIsOpenDialog(false)}
                   children={disableDialogContent ? disableDialogContent : <></>}
            />
        <Modal idModal="modal-setting-off-click"
                   openDialog={isOpenOffDialog}
                   variant={"delete"}
                   title={disableDialogTitle ? disableDialogTitle : <>Title</>}
                   actionAgree={() => onValueOff()}
                   actionCancel={() => setIsOpenOffDialog(false)}
                   textAgree={"Disable"}
                   onClose={() => setIsOpenOffDialog(false)}
                   children={disableDialogContent ? disableDialogContent : <></>}
            />
      </Box>
  );
}
